import { useRef, useEffect } from 'react';
import { Clock, X, Trash2 } from 'lucide-react';
import { useUrlHistory } from '@/hooks/useUrlHistory';
import { useLinkPreview } from '@/hooks/useLinkPreview';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface UrlHistoryDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToUrl: (url: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

interface HistoryItemProps {
  item: { url: string; title?: string; visitedAt: number };
  onNavigateToUrl: (url: string) => void;
  onRemoveUrl: (e: React.MouseEvent, url: string) => void;
  formatUrl: (url: string) => string;
  formatTime: (timestamp: number) => string;
}

function HistoryItem({ item, onNavigateToUrl, onRemoveUrl, formatUrl, formatTime }: HistoryItemProps) {
  // Only use link preview if we don't have a title stored in history
  const shouldUseLinkPreview = !item.title;
  const linkPreview = useLinkPreview(item.url);

  // Use cached title if available, otherwise fall back to stored title or formatted URL
  const displayTitle = shouldUseLinkPreview && linkPreview.data?.title
    ? linkPreview.data.title
    : item.title || formatUrl(item.url);

  return (
    <div
      className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
      onClick={() => onNavigateToUrl(item.url)}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {displayTitle}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {formatUrl(item.url)} • {formatTime(item.visitedAt)}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => onRemoveUrl(e, item.url)}
        className="h-6 w-6 p-0 ml-2 text-gray-400 hover:text-red-600 flex-shrink-0"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

export function UrlHistoryDropdown({
  isOpen,
  onClose,
  onNavigateToUrl,
  inputRef
}: UrlHistoryDropdownProps) {
  const { history, removeFromHistory, clearHistory } = useUrlHistory();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, inputRef]);

  if (!isOpen || history.length === 0) {
    return null;
  }

  const handleNavigateToUrl = (url: string) => {
    onNavigateToUrl(url);
    onClose();
  };

  const handleRemoveUrl = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    removeFromHistory(url);
  };

  const handleClearAll = () => {
    clearHistory();
    onClose();
  };

  const formatUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname + urlObj.pathname;
    } catch {
      return url;
    }
  };

  const formatTime = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div
      ref={dropdownRef}
      className={cn(
        "absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-80 overflow-hidden",
        "animate-in fade-in-0 zoom-in-95 duration-100"
      )}
    >
      <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Recent URLs
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          className="h-6 px-2 text-xs text-gray-500 hover:text-red-600"
        >
          <Trash2 className="h-3 w-3 mr-1" />
          Clear all
        </Button>
      </div>

      <div className="max-h-64 overflow-y-auto">
        {history.map((item) => (
          <HistoryItem
            key={item.url}
            item={item}
            onNavigateToUrl={handleNavigateToUrl}
            onRemoveUrl={handleRemoveUrl}
            formatUrl={formatUrl}
            formatTime={formatTime}
          />
        ))}
      </div>
    </div>
  );
}